import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuthStore, getRoleLabel, hasMinimumRole, ROLES } from '../../lib/auth';
import {
  LayoutDashboard,
  Building2,
  ClipboardCheck,
  AlertTriangle,
  FileSearch,
  Users,
  Calendar,
  Bell,
  Settings,
  LogOut,
  Menu,
  X,
  Hammer,
  Brain,
  GraduationCap,
  MonitorPlay,
  Siren,
  BarChart3,
  Shield,
  Clock3,
  Timer,
} from 'lucide-react';
import { Button } from '../ui/button';
import PasswordChangeDialog from '../PasswordChangeDialog';
import ProfileUpdateDialog from '../ProfileUpdateDialog';

const navItems = [
  { 
    path: '/dashboard', 
    icon: LayoutDashboard, 
    label: 'Dashboard',
    roles: [ROLES.SUPER_ADMIN, ROLES.OPS, ROLES.GM, ROLES.MANAGER, ROLES.STAFF]
  },
  { 
    path: '/venues', 
    icon: Building2, 
    label: 'Venues',
    roles: [ROLES.SUPER_ADMIN, ROLES.OPS, ROLES.GM, ROLES.MANAGER]
  },
  { 
    path: '/checklists', 
    icon: ClipboardCheck, 
    label: 'Checklists',
    roles: [ROLES.SUPER_ADMIN, ROLES.OPS, ROLES.GM, ROLES.HEAD_CHEF, ROLES.MANAGER, ROLES.STAFF]
  },
  { 
    path: '/checklist-builder', 
    icon: Hammer, 
    label: 'Builder',
    roles: [ROLES.SUPER_ADMIN, ROLES.OPS, ROLES.GM, ROLES.HEAD_CHEF, ROLES.MANAGER],
    requiresPermission: 'can_create_checklists'
  },
  { 
    path: '/tasks', 
    icon: AlertTriangle, 
    label: 'Tasks',
    roles: [ROLES.SUPER_ADMIN, ROLES.OPS, ROLES.GM, ROLES.HEAD_CHEF, ROLES.MANAGER]
  },
  {
    path: '/venue-intelligence',
    icon: Brain,
    label: 'Intelligence',
    roles: [ROLES.SUPER_ADMIN, ROLES.OPS, ROLES.GM, ROLES.HEAD_CHEF, ROLES.MANAGER]
  },
  {
    path: '/training',
    icon: GraduationCap,
    label: 'Training',
    roles: [ROLES.SUPER_ADMIN, ROLES.OPS, ROLES.GM, ROLES.HEAD_CHEF, ROLES.MANAGER]
  },
  {
    path: '/incidents',
    icon: Siren,
    label: 'Incidents',
    roles: [ROLES.SUPER_ADMIN, ROLES.OPS, ROLES.GM, ROLES.HEAD_CHEF, ROLES.MANAGER, ROLES.STAFF]
  },
  {
    path: '/performance-insights',
    icon: BarChart3,
    label: 'Performance',
    roles: [ROLES.SUPER_ADMIN, ROLES.OPS, ROLES.GM, ROLES.HEAD_CHEF, ROLES.MANAGER, ROLES.STAFF]
  },
  {
    path: '/clock',
    icon: Clock3,
    label: 'Clock In / Out',
    roles: [ROLES.SUPER_ADMIN, ROLES.OPS, ROLES.GM, ROLES.HEAD_CHEF, ROLES.MANAGER, ROLES.STAFF]
  },
  {
    path: '/time-attendance',
    icon: Timer,
    label: 'Time & Attendance',
    roles: [ROLES.SUPER_ADMIN, ROLES.OPS, ROLES.MANAGER]
  },
  {
    path: '/compliance',
    icon: Shield,
    label: 'EHO-Shield',
    roles: [ROLES.SUPER_ADMIN, ROLES.OPS, ROLES.GM, ROLES.HEAD_CHEF, ROLES.MANAGER]
  },
  {
    path: '/kiosk-screen',
    icon: MonitorPlay,
    label: 'Kiosk Screen',
    roles: [ROLES.SUPER_ADMIN, ROLES.OPS, ROLES.GM, ROLES.MANAGER]
  },
  { 
    path: '/audits', 
    icon: FileSearch, 
    label: 'Audits',
    roles: [ROLES.SUPER_ADMIN, ROLES.OPS, ROLES.GM, ROLES.MANAGER]
  },
  { 
    path: '/staff-hub', 
    icon: Calendar, 
    label: 'Staff Hub',
    roles: [ROLES.SUPER_ADMIN, ROLES.OPS, ROLES.GM, ROLES.HEAD_CHEF, ROLES.MANAGER, ROLES.STAFF]
  },
  { 
    path: '/users', 
    icon: Users, 
    label: 'Users',
    roles: [ROLES.SUPER_ADMIN, ROLES.OPS, ROLES.GM]
  },
];

export const Sidebar = ({ isOpen, setIsOpen }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Filter nav items based on role AND permissions
  const userPermissions = user?.custom_permissions || [];
  const isAdminOrOps = user?.role === ROLES.SUPER_ADMIN || user?.role === ROLES.OPS;
  
  const filteredNavItems = navItems.filter(item => {
    // First check role
    if (!item.roles.includes(user?.role)) return false;
    
    // Then check permission requirement (admins/ops bypass this)
    if (item.requiresPermission && !isAdminOrOps) {
      return userPermissions.includes(item.requiresPermission);
    }
    
    return true;
  });

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed md:static inset-y-0 left-0 z-50
        w-64 bg-[#0A0A0A] border-r border-[#3F3F46]
        transform transition-transform duration-200 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
        flex flex-col
      `}>
        {/* Logo */}
        <div className="p-6 border-b border-[#3F3F46]">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-heading text-xl font-bold text-[#FF6600] uppercase tracking-tight">
                RAISE THE BAR
              </h1>
              <p className="text-[#52525B] text-xs mt-1">Operations Platform</p>
            </div>
            <button 
              onClick={() => setIsOpen(false)}
              className="md:hidden text-[#A1A1AA] hover:text-white"
              data-testid="close-sidebar-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {filteredNavItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={() => setIsOpen(false)}
              className={({ isActive }) => `
                flex items-center gap-3 px-4 py-3 rounded-md
                font-medium text-sm transition-all touch-target
                ${isActive 
                  ? 'bg-[#FF6600]/10 text-[#FF6600] border-l-2 border-[#FF6600]' 
                  : 'text-[#A1A1AA] hover:bg-[#1E1E1E] hover:text-white'
                }
              `}
              data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* User Section */}
        <div className="p-4 border-t border-[#3F3F46]">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-[#FF6600] flex items-center justify-center">
              <span className="text-white font-bold text-sm">
                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium text-sm truncate">{user?.name}</p>
              <p className="text-[#52525B] text-xs">{getRoleLabel(user?.role)}</p>
            </div>
          </div>
          <div className="space-y-2 mb-3">
            <Button
              variant="ghost"
              onClick={() => setProfileDialogOpen(true)}
              size="sm"
              className="w-full justify-start text-[#A1A1AA] hover:text-white hover:bg-[#1E1E1E] text-xs"
            >
              <Settings className="w-4 h-4 mr-2" />
              Edit Profile
            </Button>
            <Button
              variant="ghost"
              onClick={() => setPasswordDialogOpen(true)}
              size="sm"
              className="w-full justify-start text-[#A1A1AA] hover:text-white hover:bg-[#1E1E1E] text-xs"
            >
              <Settings className="w-4 h-4 mr-2" />
              Change Password
            </Button>
          </div>
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="w-full justify-start text-[#A1A1AA] hover:text-[#FF3B30] hover:bg-[#FF3B30]/10"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>

        {/* Dialogs */}
        <PasswordChangeDialog 
          open={passwordDialogOpen} 
          onOpenChange={setPasswordDialogOpen} 
        />
        <ProfileUpdateDialog 
          open={profileDialogOpen} 
          onOpenChange={setProfileDialogOpen}
          userId={user?.id}
        />
      </aside>
    </>
  );
};

export const MobileNav = ({ onMenuClick }) => {
  const { user } = useAuthStore();
  
  return (
    <div className="md:hidden fixed top-0 left-0 right-0 z-30 bg-[#0A0A0A] border-b border-[#3F3F46]">
      <div className="flex items-center justify-between p-4">
        <button 
          onClick={onMenuClick}
          className="text-white touch-target flex items-center justify-center"
          data-testid="mobile-menu-btn"
        >
          <Menu className="w-6 h-6" />
        </button>
        <h1 className="font-heading text-lg font-bold text-[#FF6600] uppercase">RTB</h1>
        <NavLink to="/notifications" className="text-[#A1A1AA] hover:text-white touch-target flex items-center justify-center">
          <Bell className="w-5 h-5" />
        </NavLink>
      </div>
    </div>
  );
};

export default Sidebar;
